import classNames from 'classnames';
import React from 'react';

export interface TextFieldProps {
  id?: string;
  value: any;
  placeholder: string;
  onChange: (value: string) => any;
  name?: string;
  error?: string | boolean;
  type?: string;
  min?: number;
  max?: number;
  step?: number | string;
  disabled?: boolean;
  onFocus?: (evt: React.FocusEvent<HTMLInputElement>) => any;
  onBlur?: (evt: React.FocusEvent<HTMLInputElement>) => any;
  onSubmit?: () => any;
  iconStart?: React.ReactNode;
  iconEnd?: React.ReactNode;
  prefix?: React.ReactNode;
}

export default function TextField(props: TextFieldProps) {
  let {
    id,
    value = '',
    onChange,
    type,
    min,
    max,
    step,
    placeholder,
    onFocus,
    onBlur,
    onSubmit,
    disabled,
    error,
    iconStart,
    iconEnd,
    prefix,
    name,
  } = props;
  let ref = React.useRef<any>();

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (onChange) {
      onChange(e.target.value);
    }
  };

  const handleBlur = (evt: React.FocusEvent<HTMLInputElement>) => {
    if (onBlur) {
      onBlur(evt);
    }
  };

  const handleFocus = (evt: React.FocusEvent<HTMLInputElement>) => {
    if (onFocus) {
      onFocus(evt);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      if (ref && ref.current) {
        ref.current.blur();
      }

      if (onSubmit) {
        onSubmit();
      }
    }
  };

  let styling = classNames(
    'rounded border shadow h-8 flex items-center justify-start overflow-hidden bg-white outline-none',
    {
      'cursor-default bg-gray-200': disabled,
      'cursor-text hover:border-gray-400': !disabled,
      'text-red-500 border-red-400 hover:border-red-600 focus-within:border-red-500 focus-within:ring-2 focus-within:ring-red-200':
        !!error && !disabled,
      'focus-within:border-blue-500 focus-within:ring-2 focus-within:ring-blue-200': !disabled && !error,
    }
  );

  return (
    <div
      className={styling}
      onFocus={() => {
        if (ref && ref.current) {
          ref.current.focus();
        }
      }}
      tabIndex={!disabled ? 0 : undefined}
      role="textbox"
    >
      {iconStart && <div>{iconStart}</div>}
      {prefix && (
        <div
          className="pl-2 mr-1 select-none shrink-0"
          onMouseDown={(e) => {
            e.preventDefault();
            ref.current?.focus();
          }}
        >
          {prefix}
        </div>
      )}
      <input
        className={classNames('w-full bg-transparent h-full text-gray-800 rounded outline-none', {
          'px-2': !prefix,
          'pr-2 pl-1': prefix,
          'cursor-text': !disabled,
        })}
        id={id}
        value={value}
        placeholder={placeholder}
        onChange={handleChange}
        type={type}
        min={min}
        max={max}
        step={step}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        ref={ref}
        name={name}
        disabled={disabled}
        tabIndex={-1}
        autoComplete="off"
      ></input>
      {iconEnd && <div>{iconEnd}</div>}
    </div>
  );
}
